import { Link } from "@tanstack/react-router";
import { AlertTriangle, Plus } from "lucide-react";
import {
  BarRow,
  DeltaValue,
  MetaText,
  ScoreValue,
  StatusPill,
} from "@/components/plan-audit/AuditPrimitives";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type SolutionDetail, type SolutionVersion, useSolution } from "@/hooks/use-plan-audit";
import {
  bandColorVar,
  formatDate,
  formatDuration,
  formatScore,
  formatShortDate,
  scoreBand,
} from "@/lib/plan-audit-format";

/**
 * One solution: its plan versions, the runs against each, and how the score
 * moved by Guide group between the two most recent scored runs.
 */
export function SolutionDetailView({ slug }: { slug: string }) {
  const { data: detail, isLoading, isError, error, refetch } = useSolution(slug);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-56 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
    );
  }
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!detail) return <ErrorState message="Solution not found" />;

  const scoredRuns = detail.versions
    .flatMap((v) => v.runs)
    .filter((r) => r.overallScore !== null)
    .reverse();

  return (
    <>
      <div className="pa-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--brand-primary)]">
              {detail.name}
            </h1>
            <MetaText>
              {detail.sector} · {detail.stage} · {detail.versions.length} plan version
              {detail.versions.length === 1 ? "" : "s"}
            </MetaText>
          </div>
          <Button asChild>
            <Link to="/new-audit" search={{ solution: detail.slug }}>
              <Plus aria-hidden="true" />
              Audit a new version
            </Link>
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <ScoreTrend runs={scoredRuns} />
        <GroupMovement detail={detail} />
      </div>

      <div className="pa-card mt-4">
        <div className="pa-card-head">
          <h2>Plan versions</h2>
          <MetaText>Newest first</MetaText>
        </div>
        {detail.versions.length === 0 ? (
          <EmptyState title="No plan versions yet" description="Upload a plan to audit it." />
        ) : (
          <div className="flex flex-col gap-3">
            {detail.versions.map((version) => (
              <VersionCard key={version.id} version={version} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** Score across plan versions — the point of keeping versions at all. */
function ScoreTrend({
  runs,
}: {
  runs: { id: string; reference: string; overallScore: number | null; startedAt: string | null }[];
}) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>Score across versions</h2>
      </div>
      {runs.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">No completed run has produced a score yet.</p>
      ) : (
        <div className="flex h-44 items-end gap-4">
          {runs.map((run) => {
            const band = scoreBand(run.overallScore);
            return (
              <div key={run.id} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                <span
                  className="text-xs font-semibold tabular-nums"
                  style={{ color: bandColorVar(band) }}
                >
                  {formatScore(run.overallScore)}
                </span>
                <div
                  className="w-full rounded-t-md"
                  style={{
                    height: `${(run.overallScore ?? 0) * 10}%`,
                    minHeight: 4,
                    background: bandColorVar(band),
                  }}
                />
                <span className="truncate text-[11px] text-[var(--fg-3)]">
                  {formatShortDate(run.startedAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupMovement({ detail }: { detail: SolutionDetail }) {
  const movement = detail.movement.filter((m) => m.current !== null);

  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>Movement by group</h2>
        <MetaText>Two most recent scored runs</MetaText>
      </div>

      {detail.movementCrossesGuideVersions && (
        <div
          className="mb-3 flex gap-2 rounded-lg border p-3"
          style={{
            background: "color-mix(in srgb, var(--warning-bg) 55%, white)",
            borderColor: "color-mix(in srgb, var(--warning) 25%, white)",
          }}
        >
          <AlertTriangle
            aria-hidden="true"
            size={16}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--warning)" }}
          />
          {/* A score only means something against the standard that produced it,
              so a cross-version comparison is shown WITH this caveat rather than
              hidden or silently presented as like-for-like. */}
          <p className="text-xs leading-relaxed" style={{ color: "var(--warning-fg)" }}>
            These two runs scored against <b>different Guide versions</b>. The movement is still
            shown, but part of it may be the standard changing rather than the plan improving.
          </p>
        </div>
      )}

      {movement.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">
          Movement needs two runs that scored sections. This solution has fewer, so there is nothing
          to compare yet.
        </p>
      ) : (
        <div>
          {movement.map((group) => (
            <div key={group.group} className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <BarRow
                  label={group.group}
                  value={formatScore(group.current)}
                  fraction={(group.current ?? 0) / 10}
                  tone={bandColorVar(scoreBand(group.current))}
                />
              </div>
              <DeltaValue delta={group.delta} className="w-12 shrink-0 text-right text-xs" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionCard({ version }: { version: SolutionVersion }) {
  return (
    <div className="rounded-xl border border-[var(--border-1)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-[15px] font-bold text-[var(--brand-primary)]">
              {version.label}
            </span>
            {version.tag.length > 0 && <MetaText>{version.tag}</MetaText>}
          </div>
          <MetaText>
            {version.source} · {version.intakeSummary} · added {formatDate(version.createdAt)}
          </MetaText>
        </div>
      </div>

      {version.runs.length === 0 ? (
        <p className="mt-3 text-xs text-[var(--fg-3)]">
          This version was never audited. It is kept so the history stays complete.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {version.runs.map((run) => {
            const openable = run.status === "complete" || run.status === "failed";
            const body = (
              <>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[11px] text-[var(--fg-3)]">
                      {run.reference}
                    </span>
                    <StatusPill status={run.status} />
                    {run.firstPass && (
                      <span
                        className="rounded px-1.5 py-0.5 text-[9.5px] font-bold tracking-wider"
                        style={{
                          background: "var(--bg-accent)",
                          color: "var(--brand-accent-hover)",
                        }}
                        title="The plan as generated, before anyone edited it"
                      >
                        FIRST PASS
                      </span>
                    )}
                  </span>
                  <MetaText>
                    Guide {run.guideLabel} · {formatDate(run.startedAt)}
                    {run.durationSeconds !== null && ` · ${formatDuration(run.durationSeconds)}`}
                    {run.flagCount > 0 &&
                      ` · ${run.flagCount} flag${run.flagCount === 1 ? "" : "s"} (${run.hallucinationCount} hallucination, ${run.gapCount} gap)`}
                    {run.status === "failed" &&
                      run.failureReason.length > 0 &&
                      ` · ${run.failureReason}`}
                    {!run.hasSectionDetail &&
                      run.status === "complete" &&
                      " · no section breakdown stored"}
                  </MetaText>
                </span>
                <ScoreValue score={run.overallScore} size="sm" />
              </>
            );

            if (!openable) {
              return (
                <div
                  key={run.id}
                  className="flex items-center gap-3 rounded-lg bg-[var(--bg-2)] px-3 py-2"
                >
                  {body}
                </div>
              );
            }
            return (
              <Link
                key={run.id}
                to="/runs/$reference"
                params={{ reference: run.reference }}
                className="flex items-center gap-3 rounded-lg px-3 py-2 no-underline transition-colors hover:bg-[var(--bg-2)]"
              >
                {body}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
